import _ from '@lodash';
import i18n from 'i18n';
import * as yup from 'yup';
import { format, parse, setMinutes, setSeconds } from 'date-fns';
import {
	type App,
	type InboxItem,
	type JobItem,
	type JobQueue,
	type LicenseGroupData,
	type PendingUser,
	type Notification,
	type Profile,
	type Role,
	type User,
	type UserGroup,
	type Acl,
	type PrintGroup,
} from 'app/store/types';
import { getPlanDomain } from 'app/utils/tenant-plan';
import { type CalculatedUserPreferences } from 'app/store/reducers';
import { reportTypeOptions } from './reports.helpers';

export const DEFAULT_WORKFLOWS_GROUP_ID = '10000001100110011001100000000001'; // '11111111111111111111111111111111'; // 10000001-1001-1001-1001-100000000001
export const SAMPLE_WORKFLOWS_GROUP_ID = '01111110011001100110011111111110'; // '11111111111111111111111111111111'; // 10000001-1001-1001-1001-100000000001
export const SAMPLE_FORMS_GROUP_ID = '00011110011001100110011111111110';

export const SAMPLE_WORKFLOWS_GROUP = {
	id: SAMPLE_WORKFLOWS_GROUP_ID,
	name: i18n.t('Sample Workflows'),
	groupOrder: undefined,
	collapse: undefined,
	dateUpdated: Date.now(),
	acl: {
		users: [],
		groups: [],
		devices: [],
		deviceGroups: [],
	},
};

export const SAMPLE_FORMS_GROUP = {
	id: SAMPLE_FORMS_GROUP_ID,
	title: i18n.t('Sample Forms'),
	groupOrder: undefined,
	collapse: undefined,
	dateUpdated: Date.now(),
} as const;

export const ALL_USER_GROUP = {
	id: '00000000-d06b-44cb-9e8f-000000000000',
	name: i18n.t('All Groups'),
	dateUpdated: 0,
	roles: [],
} as UserGroup;

export const collabExtensionStatuses = {
	approved: 'Approved',
	rejected: 'Denied',
	pending: 'Pending',
};

export const notificationColorToCssProperty = ({ color }: { color: Notification['color'] }) =>
	({
		blue: '#0776F5',
		green: '#51D15E',
		orange: '#FFB300',
		pink: '#F1B1F3',
		purple: '#A0A1F6',
		red: '#F44336',
	})[color];

export const hoursOnly = (date: Date) => {
	return setMinutes(setSeconds(date, 0), 0);
};

export const isValidUrl = (value: string) => yup.string().url().required().isValidSync(value);

export const isValidEmail = (value: string) =>
	yup
		.string()
		.matches(/^[a-zA-Z0-9](?!.*([._%+-])\1)[a-zA-Z0-9._%+-]*@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)
		.required()
		.max(254)
		.isValidSync(value);

// test for special characters and whitespace
export const isValidName = (value: string) => {
	if (noSpecialCharacters(value) && !isOnlyWhitespace(value)) {
		return true;
	}
	return false;
};

export const isValidPhoneNumber = (value: string) => /^[0-9.-]*$/.test(value);
// FIXME::Regex should be /^([0-9]+-?)*$/

export const isValidSlug = (value: string) => /^[a-zA-Z0-9]+([a-zA-Z0-9-]+[a-zA-Z0-9]+)?$/.test(value);

const isValidPrintGroupSlug = isValidSlug;

export const isValidAndNotRestrictedPrintGroupSlug = (value: string) =>
	isValidPrintGroupSlug(value) && value !== 'my-print-queue';

export const isValidDate = (value: Date | string | number | null | undefined) => {
	if (!value) {
		return false;
	}
	try {
		const date = new Date(value);
		return !isNaN(date.getTime());
	} catch {
		return false;
	}
};

export const isOnlyWhitespace = (value: string) => value.match(/^ *$/) !== null;
export const noSpecialCharacters = (value: string) => !/[~`!#$%^&*+=\-[\]\\';,/{}|\\":<>?]/g.test(value);

export const getSubdomainsInReverseOrder = (url: string) => {
	url = url.replace(/(localhost|\[::1\]|127(?:\.[0-9]+){0,2}\.[0-9]+)(:\d+)?$/, 'localhost.com'); // fake "normal" localhost URL for easier parsing

	const { hostname } = new URL(url);

	const parts = hostname.split('.');

	const subdomainsInReverseOrder = parts.slice(0, -2).reverse();

	// treat `www` off the root as a fake subdomain
	if (subdomainsInReverseOrder[0] === 'www') {
		subdomainsInReverseOrder.shift();
	}

	return subdomainsInReverseOrder;
};

export const isRootUrl = (url: string) => getSubdomainsInReverseOrder(url).length === 0;
export const isLicenseGroupUrl = (url: string) => {
	const subdomains = getSubdomainsInReverseOrder(url);
	return subdomains.length === 2 && subdomains[0] === 'tenant';
};

export const getRootDomain = (staticDomain = false) => {
	// DEV NOTE::when we first built this project we only deployed the services to one region,...
	// ...and _which_ region it happened to be was different per environment
	// HACK-ish::TEMP::to my knowledge we _will_ eventually just deploy these services to both regions
	if (staticDomain) {
		return getPlanDomain(process.env.REACT_APP_ENVIRONMENT === 'production' ? 'stc' : 'stratus');
	}

	const url = window.location.host;

	return url
		.split('.')
		.reverse()
		.slice(0, url.match(/(localhost|\[::1\]|127(?:\.[0-9]+){0,2}\.[0-9]+)(:\d+)?$/) ? 1 : 2)
		.reverse()
		.join('.');
};

export const sanitizeFilename = (name: string) => name.replace(/[^\p{L}\p{N}\s_-]/gu, '');

// export const getGenerationByFamily = (family: string): Device['generation'] => {
// 	// only use beginning of family name (for some reason it seems the family sometimes has a longer name)
// 	switch (family.split('_')[0]) {
// 		case 'Kronos':
// 		case 'Venus':
// 			return 'A4';
// 		case 'Amur':
// 		case 'Donau':
// 		case 'DonauBK':
// 		case 'Taiga':
// 			return 'IT3';
// 		case 'KronosS':
// 		case 'Poseidon':
// 		case 'ZeusS':
// 		case 'Helios':
// 		case 'MinervaSBK':
// 		case 'ZeusSZX0':
// 		case 'ZeusSBK':
// 		case 'VenusMLK':
// 			return 'IT5';
// 		case 'Altair':
// 		case 'Cypress':
// 		case 'DenebMLK':
// 		case 'EagleBKH':
// 		case 'EagleBKL':
// 		case 'EagleL':
// 		case 'EagleH':
// 		case 'EagleZPlus':
// 		case 'HeliosMLK':
// 		case 'Maple':
// 		case 'Rosewood':
// 		case 'Sparrow':
// 		case 'SparrowBK':
// 			return 'IT6';
// 		case 'WHUB':
// 			return 'workplacehub';
// 		default:
// 			return 'IT4';
// 	}
// };

export const arrayify = <T>(value: T | T[]): T[] => (Array.isArray(value) ? value : [value]);

export const chunkify = (arr: any[], size: number) => {
	const chunkArr = [];
	for (let i = 0; i < arr.length; i += size) {
		const chunk = arr.slice(i, i + size);
		chunkArr.push(chunk);
	}
	return chunkArr;
};

export const convertLocalToUTCHack = (date: Date) => {
	return new Date(date.getTime() + date.getTimezoneOffset() * 60000);
};

export const convertUTCHacktoLocal = (date: Date) => {
	const now = new Date();
	return new Date(date.getTime() - now.getTimezoneOffset() * 60000);
};

const deepOmitNullish = (obj: { [key: string]: any }): { [key: string]: any } =>
	Object.entries(obj)
		.filter(([_key, value]) => !_.isNil(value))
		.reduce(
			(acc, [key, value]) => ({
				...acc,
				[key]: _.isObject(value) ? deepOmitNullish(value) : value,
			}),
			{},
		);

export const isEqualOmitNullish = (a: { [key: string]: any }, b: { [key: string]: any }) =>
	_.isEqual(deepOmitNullish(a), deepOmitNullish(b));

export const deepSome = (obj: { [key: string]: any }): boolean => {
	if (typeof obj === 'boolean') {
		return obj;
	}

	return Object.values(obj).some((value) => (typeof value === 'boolean' ? value : deepSome(value)));
};

export const toHHMM = (time: string) => {
	if (!time) {
		return '';
	}
	let [HH, MM] = time.split(':');
	if (Number(HH) < 10 && !HH.startsWith('0')) {
		HH = `0${HH}`;
	} else if (Number(HH) === 0) {
		HH = '00';
	}
	if (Number(MM) < 10 && !MM.startsWith('0')) {
		MM = `0${MM}`;
	} else if (Number(MM) === 0) {
		MM = '00';
	}

	return `${HH}:${MM}`;
};

// FIXME::need to combine `responseError` and `responseErrors` or just...make this less nonsense
type ResponseErrorsTypeA = { data: { returnCode: string }[] }[];
type ResponseErrorsTypeB = { data: { errors?: { add?: string[]; delete?: string[] } } }[];
const isResponseErrorsTypeA = (
	responses: ResponseErrorsTypeA | ResponseErrorsTypeB | any,
): responses is ResponseErrorsTypeA => !!responses?.[0]?.data?.[0]?.returnCode;
const isResponseErrorsTypeB = (
	responses: ResponseErrorsTypeA | ResponseErrorsTypeB | any,
): responses is ResponseErrorsTypeB => {
	const errors = responses?.[0]?.data?.errors;
	return !!(errors?.add || errors?.delete);
};
export const responseErrors = (responses: ResponseErrorsTypeA | ResponseErrorsTypeB | unknown) => {
	responses = arrayify(responses); // HACK-ish::just turn into an array if not one (this may need to be smartened up) - we can get away with this due to the `unknown` type
	if (isResponseErrorsTypeA(responses)) {
		const errors = responses
			.map(({ data }) => data)
			.flat()
			.filter(({ returnCode }) => !`${returnCode}`.startsWith('2'));
		if (errors.length) console.error(errors);
		return errors;
	}
	if (isResponseErrorsTypeB(responses)) {
		const errors = responses
			.map(({ data }) => (data.errors ? [...(data.errors.add || []), ...(data.errors.delete || [])] : []))
			.flat();
		if (errors.length) console.error(errors);
		return errors;
	}
	return [];
};

type ErrorOutput = {
	payload: {
		error: string;
		message: string;
		statusCode: number;
	};
	statusCode: number;
};
type ResponseError = { output: ErrorOutput }[] | null;
export const responseError = ({ errors }: { errors: ResponseError }) => {
	const error = errors?.[0]?.output;
	if (error !== undefined) {
		console.error(error);
	}
	return error;
};

export type UnmassagedAcl = {
	users: { [userId: string]: { role: Acl['users'][number]['role'] } };
	userGroups: { [userGroupId: string]: { role: Acl['groups'][number]['role'] } };
	devices?: { [deviceId: string]: { role: Acl['devices'][number]['role'] } };
	deviceGroups?: { [deviceGroupId: string]: { role: Acl['deviceGroups'][number]['role'] } };
};

// DEV NOTE::WARNING::this will only work with bulk actions if the assumption that you're the owner of all the records...which is possibly 100% wrong
export const massageUnmassagedAcl = (
	prevAcl: Pick<Acl, 'users' | 'groups'>,
	{ users, userGroups, devices, deviceGroups }: UnmassagedAcl,
	usersOnlyAcl = true,
) => {
	const aclUsers = [
		...Object.entries(users).map(([userId, { role }]) => ({
			name: userId,
			role,
		})),
		// also include the owner
		prevAcl.users.find((user) => user.role === 'owner')!,
	];

	const aclGroups = Object.entries(userGroups).map(([userGroupId, { role }]) => ({
		name: userGroupId,
		role,
	}));

	const aclDevices = devices
		? Object.entries(devices).map(([deviceId, { role }]) => ({
				name: deviceId,
				role,
			}))
		: [];

	const aclDeviceGroups = deviceGroups
		? Object.entries(deviceGroups).map(([deviceGroupId, { role }]) => ({
				name: deviceGroupId,
				role,
			}))
		: [];

	if (usersOnlyAcl) {
		const ret: Pick<Acl, 'users' | 'groups'> = {
			...prevAcl,
			users: aclUsers,
			groups: aclGroups,
		};
		return ret;
	}
	const ret: Acl = {
		...prevAcl,
		users: aclUsers,
		groups: aclGroups,
		devices: aclDevices,
		deviceGroups: aclDeviceGroups,
	};
	return ret;
};

const DEMO_FILE_COUNT_MAX = 50;
const DEMO_FILE_SIZE_MAX = 10485760; // 10MB (10,485,760 bytes)

export const getLicenseCapacityExceeded = (licenseGroupData: LicenseGroupData) => {
	const { licenseUsage, orderType } = licenseGroupData;
	if (!licenseUsage || orderType !== 'DEMO') {
		return false;
	}

	// Get 0:00AM on based on the browser timezone
	const today = new Date().setHours(0, 0, 0, 0);

	const isLicenseCapacityExceeded = !!(
		licenseUsage?.dateUpdated &&
		licenseUsage?.dateUpdated > today &&
		(licenseUsage?.fileCount > DEMO_FILE_COUNT_MAX || licenseUsage?.fileSizeTotal > DEMO_FILE_SIZE_MAX)
	);

	return isLicenseCapacityExceeded;
};

export const EXPIRED_GRACE_PERIOD: { [key in LicenseGroupData['orderType']]: number } = {
	PRODUCTION: 30 * (24 * 3600 * 1000),
	DEMO: 7 * (24 * 3600 * 1000),
	NFR: 30 * (24 * 3600 * 1000),
};

export const convertToFormData = (obj: { [name: string]: string | Blob }) => {
	const formData = new FormData();
	Object.entries(obj).forEach(([name, value]) => formData.append(name, value));
	return formData;
};

export const getSiteBackgroundImageWithFallbacks = ({
	licenseGroupId,
	profileId,
	siteBackgroundCache,
	appearance = 'light',
}: {
	licenseGroupId: string;
	profileId: string;
	siteBackgroundCache: number;
	appearance?: 'light' | 'dark';
}) => [
	`/api/user/${licenseGroupId}/preferences/background?key=${profileId}&v=${siteBackgroundCache}`,
	`/api/user/${licenseGroupId}/preferences/background?key=${profileId}&v=${siteBackgroundCache - 1}`,
	appearance === 'light'
		? `${process.env.PUBLIC_URL}/assets/images/featured-backgrounds/colors/light-gray.svg`
		: `${process.env.PUBLIC_URL}/assets/images/featured-backgrounds/colors/dark-gray.svg`,
];

export const reviewAppSettings = () => {
	let jntService = {
		service: 'jnt',
		reviewApp: '',
	};
	let formBuilderService = 'formbuilder';
	let formsService = 'forms-service';
	let r2mService = 'release2me-service';
	let r2mReviewApp = '';
	let wfxService = 'wfx';
	let wfxReviewApp = '';
	let metadataService = 'metadata-api';
	let loggerService = 'logger';
	let nodeTestingService = 'ntf';
	let peopleCollabService = 'people-collab-serv';
	let flopsService = 'flops';

	if (localStorage) {
		const settingsJson = localStorage.getItem('sec.review.portal.settings');
		if (settingsJson) {
			const settings = JSON.parse(settingsJson);
			if (settings?.jntService) jntService = settings.jntService;
			if (settings?.formBuilderService) formBuilderService = settings.formBuilderService;
			if (settings?.formsService) formsService = settings.formsService;
			if (settings?.['r2m.service']) r2mService = settings['r2m.service'];
			if (settings?.['r2m.reviewApp']) r2mReviewApp = settings['r2m.reviewApp'];
			if (settings?.['wfx.service']) wfxService = settings['wfx.service'];
			if (settings?.['wfx.reviewApp']) wfxReviewApp = settings['wfx.reviewApp'];
			if (settings?.metadataService) metadataService = settings.metadataService;
			if (settings?.loggerService) loggerService = settings.loggerService;
			if (settings?.nodeTestingService) nodeTestingService = settings.nodeTestingService;
			if (settings?.peopleCollabService) peopleCollabService = settings.peopleCollabService;
			if (settings?.flopsService) flopsService = settings.flopsService;
		}
	}

	return {
		jntService,
		formBuilderService,
		r2mService,
		r2mReviewApp,
		formsService,
		metadataService,
		wfxService,
		wfxReviewApp,
		loggerService,
		nodeTestingService,
		peopleCollabService,
		flopsService,
	} as const;
};

type Path = string | ConstructorParameters<typeof URLSearchParams>[0];

// accepts either a full path string or an object which is converted to a query string
const makePath = (path: Path) => (typeof path === 'string' ? path : `?${new URLSearchParams(path).toString()}`);

export const getWfxDesignerUrl = (
	path: { plan: LicenseGroupData['plan']; [key: string]: string },
	userPreferences: CalculatedUserPreferences,
) => {
	const slug = window.location.host.match(/^(.+)\.tenant\./)?.[1];

	const preferences = {
		appearance: userPreferences.appearance,
		siteBackgroundUrl: userPreferences.primarySiteBackgroundUrl,
		tableHeaderStandardBackgroundColor: userPreferences.tableHeaderStandardBackgroundColor,
		pageHeaderBackgroundColor: userPreferences.pageHeaderBackgroundColor,
		navbarBackgroundColor: userPreferences.navbarBackgroundColor,
	};

	return `web+kmdesigner://${slug}.tenant.${getRootDomain(true)}${makePath({
		...path,
		planDomain: getPlanDomain(path.plan),
		preferences: btoa(JSON.stringify(preferences)),
	})}`;
};

export const getHelpUrl = (locale: string, path: Path = '') => {
	return `https://help.${getRootDomain()}?locale=${locale.slice(0, 2)}&path=${encodeURIComponent(makePath(path))}`;
};

type Region = Profile['awsRegion'];

export const getJntUrl = (region: Region = 'us-east-1') =>
	`https://${
		reviewAppSettings().jntService.reviewApp ? `${reviewAppSettings().jntService.reviewApp}.` : ''
	}${region}.${reviewAppSettings().jntService.service}.${getRootDomain(true)}`;

export const getMetadataUrl = (region: Region = 'us-east-1') =>
	`https://${region}.${reviewAppSettings().metadataService}.${getRootDomain(true)}`;

export const getWfxUrl = (region: Region = 'us-east-1') => {
	const { wfxReviewApp, wfxService } = reviewAppSettings();
	return `https://${wfxReviewApp ? `${wfxReviewApp}.` : ''}${region}.${wfxService}.${getRootDomain(true)}`;
};

export const getFormsUrl = (region: Region = 'us-east-1') =>
	`https://${region}.${reviewAppSettings().formsService}.${getRootDomain(true)}`;

export const getR2mUrl = (region: Region = 'us-east-1') => {
	const { r2mReviewApp, r2mService } = reviewAppSettings();
	return `https://${r2mReviewApp ? `${r2mReviewApp}.` : ''}${region}.${r2mService}.${getRootDomain(true)}`;
};

export const getFormBuilderUrl = () => `https://${reviewAppSettings().formBuilderService}.${getRootDomain()}`;

export const getLogApiUrl = (region: Region = 'us-east-1') =>
	`https://${region}.${reviewAppSettings().loggerService}.${getRootDomain(true)}/v1`;

export const getNodeTestingAPI = (region: Region = 'us-east-1') =>
	`https://${region}.${reviewAppSettings().nodeTestingService}.${getRootDomain(true)}`;

export const getApproveNodesUrl = (region: Region = 'us-east-1', nameSpace: string) => {
	return nameSpace.includes('QueueEditAndApprove')
		? `https://${region}.people-queue-edit.${getRootDomain(true)}`
		: `https://${region}.p-edit-approve.${getRootDomain(true)}`;
};

export const getCollabServiceUrl = (region: Region = 'us-east-1') =>
	`https://${region}.${reviewAppSettings().peopleCollabService}.${getRootDomain(true)}`;

// WARNING::this is used on a public (non-admin) page
export const getFlopsUrl = (region: Region | string = 'us-east-1') =>
	`https://${region}.${reviewAppSettings().flopsService}.${getRootDomain(true)}`;

export const getPrintGroupUrl = (
	region: Region = 'us-east-1',
	licenseGroupSlug: LicenseGroupData['slug'],
	printGroupSlug: PrintGroup['slug'],
	userSlug: string,
	printGroupType: PrintGroup['type'],
) => {
	const printGroupFriendlySlug = printGroupType === 'personal' ? 'my-print-queue' : printGroupSlug;
	return `https://${region}.ipp.${getRootDomain()}/ipp/print/${licenseGroupSlug}/${printGroupFriendlySlug}/${userSlug}`;
};

export const getPrivacyPolicyUrl = (region: Region = 'us-east-1') =>
	region === 'ca-central-1'
		? 'https://konicaminolta.ca/en/business/siteinformation/terms-of-use'
		: 'https://kmbs.konicaminolta.us/privacy-policy';

const checkDiff = (role: any, keys: any) => {
	if (typeof role === 'boolean') {
		return !role;
	}

	for (const key of keys) {
		if (!checkDiff(role[key], Object.keys(role[key]))) {
			return false;
		}
	}
	return true;
};

const getKeys = (userPermission: any, targetPermissions: any, keys: any) => {
	if (typeof userPermission === 'boolean' && typeof targetPermissions === 'boolean') {
		return userPermission || (!userPermission && !targetPermissions);
	}
	for (const key of keys) {
		const keys1 = Object.keys(userPermission[key]);
		const keys2 = Object.keys(targetPermissions[key]);
		const diff = _.difference(keys2, keys1);
		const same = _.difference(keys2, diff);
		if (diff.length && !checkDiff(targetPermissions[key], diff)) {
			return false;
		}
		if (!getKeys(userPermission[key], targetPermissions[key], same)) {
			return false;
		}
	}
	return true;
};

export const canAssignRole = (userPermissionArg: any, targetPermissionsArg: any): boolean => {
	const userPermission = syncObjectStructure(userPermissionArg, defaultPermissions);
	const targetPermissions = syncObjectStructure(targetPermissionsArg, defaultPermissions);
	const keys1 = Object.keys(userPermission);
	const keys2 = Object.keys(targetPermissions);
	const diff = _.difference(keys2, keys1);
	const same = _.difference(keys2, diff);
	if (diff.length && !checkDiff(targetPermissions, diff)) {
		return false;
	}
	return getKeys(userPermission, targetPermissions, same);
};

const syncObjectStructure = (objectArg: any, template: any) => {
	const object = structuredClone(objectArg);
	(function addFromTemplate(obj, temp) {
		for (const prop in temp) {
			if (!(prop in obj)) obj[prop] = temp[prop];
			// Unknown to `obj`! Add it
			else if (typeof obj[prop] === 'object' && typeof temp[prop] === 'object')
				addFromTemplate(obj[prop], temp[prop]); // Nested objects! Recursion-step
		}
	})(object, template); // Start adding to `object` from `template`

	(function removeFromObject(obj, temp) {
		for (const prop in obj) {
			if (!(prop in temp)) delete obj[prop];
			// Unknown to `temp`! Remove it
			else if (typeof obj[prop] === 'object' && typeof temp[prop] === 'object')
				removeFromObject(obj[prop], temp[prop]); // Nested objects! Recursion-step
		}
	})(object, template); // Start removing properties of `object`

	return object;
};

export const formatBytes = (bytes: number, decimals = 2) => {
	if (bytes === 0) return '0 Bytes';

	const k = 1024;
	const dm = decimals < 0 ? 0 : decimals;
	const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

	const i = Math.floor(Math.log(bytes) / Math.log(k));

	return `${parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
};

export const getAppLink = (app: Pick<App, 'type' | 'workflowId' | 'nodeId' | 'url'>, tenant: LicenseGroupData) => {
	switch (app.type) {
		case 'url':
			return app.url ?? '';
		case 'internal':
			return `https://${tenant.slug}.tenant.${getRootDomain()}/admin/internalform/${tenant.region}/${
				app.workflowId
			}-${app.nodeId}`;
		case 'external':
			return app.url
				? app.url
				: `https://forms.${getRootDomain()}/externalform/${tenant.region}/${tenant.slug}/${app.workflowId}-${
						app.nodeId
					}`;
		default:
			return '';
	}
};

export const defaultPermissions = {
	devicesTab: {
		devicesSection: {
			view: false,
			add: false,
			edit: false,
			remove: false,
			activate: false,
			createGroup: false,
			editGroup: false,
			deleteGroup: false,
		},
		inputNodesSection: {
			view: false,
			viewAll: false,
			assign: false,
			assignAll: false,
		},
	},
	formsTab: {
		formsSection: {
			view: false,
			add: false,
			edit: false,
			remove: false,
			import: false,
			activate: false,
			deactivate: false,
			createGroup: false,
			editGroup: false,
			deleteGroup: false,
			clone: false,
		},
	},
	usersTab: {
		usersSection: {
			view: false,
			invite: false,
			edit: false,
			remove: false,
			assignUser: false,
			block: false,
			createGroup: false,
			editGroup: false,
			deleteGroup: false,
			assignGroup: false,
			transferOwnership: false,
			resetPassword: false,
		},
	},
	workflowsTab: {
		workflowsSection: {
			view: false,
			create: false,
			edit: false,
			remove: false,
			share: false,
			import: false,
			run: false,
			changeOwner: false,
			createGroup: false,
			shareGroup: false,
			editGroup: false,
			deleteGroup: false,
			clone: false,
			samples: false,
		},
	},
	appsTab: {
		internalSection: {
			create: false,
			view: false,
			delete: false,
			share: false,
		},
		externalSection: {
			create: false,
			view: false,
			delete: false,
			share: false,
		},
		urlSection: {
			create: false,
			view: false,
			delete: false,
			share: false,
		},
	},
	dataTab: {
		reportsSection: {
			view: false,
		},
		logsSection: {
			view: false,
		},
	},
	jobsTab: {
		jobQueueSection: {
			view: false,
			create: false,
			edit: false,
			remove: false,
			activate: false,
		},
		trackerSection: {
			view: false,
			track: false,
		},
		tasksSection: {
			view: false,
		},
		namingSection: {
			view: false,
		},
	},
	rolesTab: {
		rolesSection: {
			view: false,
			create: false,
			edit: false,
			remove: false,
		},
	},
	settingsTab: {
		settingsSection: {
			view: false,
			edit: false,
		},
		profileSection: {
			outOfOffice: false,
		},
		systemSection: {
			manageNotifications: false,
		},
	},
	r2mTab: {
		personalQueueSection: {
			view: false,
			deleteJobs: false,
			delegateJobs: false,
			use: false,
		},
		devicesSection: {
			view: false,
			manageDevices: false,
		},
		printSettingsSection: {
			view: false,
			setPreferences: false,
		},
		sharedQueueSection: {
			view: false,
			create: false,
			edit: false,
			delete: false,
			deleteJobs: false,
			manageUsers: false,
			use: false,
		},
		guestQueueSection: {
			view: false,
			create: false,
			edit: false,
			delete: false,
			deleteJobs: false,
			manageUsers: false,
		},
		directQueueSection: {
			view: false,
			create: false,
			edit: false,
			delete: false,
			deleteJobs: false,
			manageUsers: false,
			use: false,
		},
		r2mInQueueSection: {
			view: false,
			create: false,
			edit: false,
			delete: false,
			deleteJobs: false,
			manageUsers: false,
			use: false,
		},
		systemQueueSection: {
			view: false,
			create: false,
			edit: false,
			delete: false,
			deleteJobs: false,
			manageUsers: false,
			use: false,
		},
		systemPrintQueueSection: {
			view: false,
			deleteFiles: false,
			setPreferences: false,
		},
	},
	reportsTab: reportTypeOptions.reduce(
		(acc, type) => {
			acc[`${type}Section`] = {
				generate: false,
				export: false,
			};
			return acc;
		},
		{} as { [key in `${(typeof reportTypeOptions)[number]}Section`]: { generate: boolean; export: boolean } },
	),
	bsnTab: {
		dashboardSection: {
			view: false,
		},
		policiesSection: {
			create: false,
			edit: false,
			delete: false,
			sendEmail: false,
		},
		policyGroupsSection: {
			create: false,
			edit: false,
			delete: false,
			sendEmail: false,
		},
	},
	authTab: {
		systemSection: {
			view: false,
			addProvider: false,
			editProvider: false,
			removalTool: false,
		},
		devicesSection: {
			register: false,
		},
		userSection: {
			addCards: false,
			importCards: false,
			managePins: false,
		},
	},
	documentsTab: {
		archivedJobsSection: {
			view: false,
			downloadReport: false,
			downloadReportAndFiles: false,
			delete: false,
		},
		storageTrashSection: {
			view: false,
			restore: false,
			deletePermanently: false,
		},
	},
};

type PathLeaves<T> = T extends object
	? {
			[K in keyof T]: `${Exclude<K, symbol>}${PathLeaves<T[K]> extends never ? '' : `.${PathLeaves<T[K]>}`}`;
		}[keyof T]
	: never;

export type UserPermissionString = PathLeaves<typeof defaultPermissions>;

export const errorCodes = {
	fileUploadFail: '10100',
	fileUploadFailOnRead: '10101',
	fileUploadFailOnWrite: '10102',
	fileUploadFailAuth: '10103',
	fileUploadFailOnConflict: '10104',
	fileUploadFailOnForbidden: '10105',
	fileUploadFailOnMissing: '10106',
	fileUploadFailOnCreateFolder: '10107',
	fileUploadFailOutOfSpace: '10108',
	fileUploadFailSizeLimit: '10109',
	fileUploadFailPathIsTooLong: '10110',
	fileUploadFailOnInsufficientStorage: '10111',
	fileUploadNodeDisabled: '10112',
	messageSendValidationFail: '10200',
	messageSendExceedLimit: '10201',
	messageSendFail: '10202',
	generalError: '00100',
	wfxUpdateStatusFailure: '10300',
	userErrorAlreadyAssigned: '20100',
	userErrorFailedInvite: '20102',
	userErrorPartial: '20103',
	userErrorUnexpected: '20104',
	userErrorNotFound: '20105',
	userErrorNoEmail: '20106',
	userErrorInvalidStatus: '20107',
	userErrorFailedReinvite: '20108',
	userErrorBlockUnauthorized: '20109',
	userErrorAlreadyBlocked: '20110',
	userErrorBlockPendingUser: '20111',
	userErrorBlockLastAdmin: '20112',
	userErrorBlockFailed: '20113',
	userErrorBlockUnexpected: '20114',
	userErrorUnblockUnauthorized: '20115',
	userErrorAlreadyUnblocked: '20116',
	userErrorUnblockFailed: '20117',
	userErrorUnblockUnexpected: '20118',
	deviceErrorNoSuchDevice: '30100',
	deviceErrorDeleteFailure: '30101',
	deviceErrorDeletePartial: '30102',
	deviceErrorDeleteUnexpected: '30103',
	deviceErrorAddUnauthorized: '30104',
	deviceErrorAddFailed: '30105',
	deviceErrorUnauthorized: '30106',
	deviceErrorUnsupported: '30107',
	deviceErrorAlreadyAssoc: '30108',
	deviceErrorAddPartial: '30109',
	deviceErrorAddUnexpected: '30110',
	deviceErrorValidation: '30111',
	deviceErrorLicenseFail: '30112',
	deviceErrorNoLicense: '30113',
	deviceErrorBadSchema: '30114',
	deviceErrorRemoveLicenseFail: '30115',
	appErrorNoSuchApp: '40100',
	appErrorDeleteFailure: '40101',
	appErrorDeletePartial: '40102',
	appErrorDeleteUnexpected: '40103',
	appErrorAddUnauthorized: '40104',
	appErrorAddFailed: '40105',
	appErrorUnauthorized: '40106',
	appErrorUnsupported: '40107',
	appErrorAlreadyAssoc: '40108',
	appErrorAddPartial: '40109',
	appErrorAddUnexpected: '40110',
	appErrorValidation: '40111',
	appErrorLicenseFail: '40112',
	appErrorNoLicense: '40113',
	appErrorBadSchema: '40114',
	appErrorRemoveLicenseFail: '40115',
	nodeDisabled: '10300',
} as const;

export function groupByID<T extends JobItem | InboxItem | JobQueue>(rawItems: T[]): { [id: string]: T } {
	const itemsByID: { [id: string]: T } = {};
	if (!rawItems || rawItems.length === 0) return itemsByID;
	rawItems.forEach((item) => {
		itemsByID[item.id] = item;
	});
	return itemsByID;
}

export const getSuperAdminRole = (roles: Role[]) => {
	return roles.find((role) => role?.systemRole === 'tenant-super-admin')!.id;
};

export const getSuperAdmins = (users: (User | PendingUser)[], roles: Role[], activeAdminsOnly = false) => {
	const superAdminRole = getSuperAdminRole(roles);
	return users.filter(
		(user) => !(activeAdminsOnly && (user.blocked || user.pending)) && user.roles.includes(superAdminRole),
	);
};

export const isEqualIgnoreOrder = (obj1: any, obj2: any): boolean => {
	// Handle null/undefined cases
	if (obj1 === obj2) return true;
	if (obj1 == null || obj2 == null) return false;

	// Handle arrays - compare contents regardless of order
	if (Array.isArray(obj1) && Array.isArray(obj2)) {
		if (obj1.length !== obj2.length) return false;

		// Create a copy of obj2 array to mark items as "used" during comparison
		const obj2Copy = [...obj2];

		// For each item in obj1, find a matching item in obj2Copy
		for (const item1 of obj1) {
			const matchIndex = _.findIndex(obj2Copy, (item2) => isEqualIgnoreOrder(item1, item2));

			// If no match was found for this item, arrays are not equal
			if (matchIndex === -1) return false;

			// Mark this item as "used" by removing it
			obj2Copy.splice(matchIndex, 1);
		}

		// All items matched
		return true;
	}

	// Handle objects (but not arrays)
	if (_.isPlainObject(obj1) && _.isPlainObject(obj2)) {
		const keys1 = Object.keys(obj1);
		const keys2 = Object.keys(obj2);

		// Check if they have the same properties
		if (!_.isEqual(new Set(keys1), new Set(keys2))) return false;

		// Recursively compare property values
		return keys1.every((key) => isEqualIgnoreOrder(obj1[key], obj2[key]));
	}

	// For primitive values, use direct comparison
	return obj1 === obj2;
};

export const formatDate = (date?: Date, fmt = 'P, pp') => {
	// const formatter = formatWithOptions({ locale: getLocale(i18next.language) }, format);
	// return formatter(date ?? new Date());
	return format(date ?? new Date(), fmt);
};

export const parseDate = (dateString: string, fmt = 'P, pp') => {
	const format = !_.isEmpty(fmt) ? fmt : 'P, pp';
	return parse(dateString || formatDate(new Date()), format, new Date());
};

export const getUserNameFormatted = (user: User) => {
	return `${user?.lastName}, ${user?.firstName}`;
};

export const arrayToObjectBool = (array: string[]) => array.reduce((acc, key) => ({ ...acc, [key]: true }), {});

export const massageTransferInfo = (rawTransferInfo: any) => {
	const transferInfo: {
		temporary: {
			redirect: boolean;
			startDate: string;
			endDate: string;
			sendEmail: boolean;
			active: boolean;
			allowLogin: boolean;
			toUserId: string;
			message?: string;
		};
		permanent: {
			startDate: string;
			sendEmail: boolean;
			transfer: boolean;
			active: boolean;
			removeUser: boolean;
			toUserId: string;
			immediate: boolean;
		};
	} = {
		temporary: {
			redirect: rawTransferInfo?.temporary?.redirect,
			startDate: rawTransferInfo?.temporary?.schedule?.startDate
				? formatDate(
						convertUTCHacktoLocal(parseDate(rawTransferInfo?.temporary?.schedule?.startDate, 'yyyyMMddHH')),
					)
				: '',
			endDate: rawTransferInfo?.temporary?.schedule?.endDate
				? formatDate(
						convertUTCHacktoLocal(parseDate(rawTransferInfo?.temporary?.schedule?.endDate, 'yyyyMMddHH')),
					)
				: '',
			sendEmail: rawTransferInfo?.temporary?.sendEmail,
			active: rawTransferInfo?.temporary?.active,
			allowLogin: rawTransferInfo?.temporary?.allowLogin,
			toUserId: rawTransferInfo?.temporary?.toPersonId,
			message: rawTransferInfo?.temporary?.message,
		},
		permanent: {
			startDate: rawTransferInfo?.permanent?.schedule?.startDate
				? formatDate(
						convertUTCHacktoLocal(parseDate(rawTransferInfo?.permanent?.schedule?.startDate, 'yyyyMMddHH')),
					)
				: '',
			sendEmail: rawTransferInfo?.permanent?.sendEmail,
			transfer: rawTransferInfo?.permanent?.transfer,
			active: rawTransferInfo?.permanent?.active,
			removeUser: rawTransferInfo?.permanent?.removeUser,
			toUserId: rawTransferInfo?.permanent?.toPersonId,
			immediate: rawTransferInfo?.permanent?.immediate,
		},
	};

	return transferInfo;
};

export const isIT5Device = (modelName: string | undefined): boolean => {
	if (!modelName) return false;

	const it5Models = [
		'C759',
		'C659',
		'C658',
		'C558',
		'C458',
		'C368',
		'C308',
		'C258',
		'C287',
		'C227',
		'658e',
		'558e',
		'458e',
		'368e',
		'308e',
		'958',
		'808',
		'558',
		'458',
		'368',
		'308',
		'367',
		'287',
		'227',
		'C3851FS',
		'C3851',
		'C3351',
		'4752',
		'4052',
		'C4080',
		'C4070',
		'C4065',
	];

	return it5Models.some((model) => modelName.includes(model));
};

export const isMfpEnvironment = (): boolean => {
	const urlParams = new URLSearchParams(window.location.search);
	const debugMfp = urlParams.get('debug_mfp');

	const stateParam = urlParams.get('state');

	const hasValidMfpState =
		stateParam &&
		(stateParam.length > 20 || // real MFP states are typically longer
			stateParam.includes('=') ||
			/^[A-Za-z0-9+/]+=*$/.test(stateParam)); // base64 pattern

	const userAgent = navigator.userAgent.toLowerCase();
	const mfpPatterns = [
		'mfp',
		'multifunction',
		'kiosk',
		'embedded',
		// add specific MFP device patterns as discovered
	];
	const hasKnownMfpAgent = mfpPatterns.some((pattern) => userAgent.includes(pattern));

	const userAgentLower = userAgent.toLowerCase();
	const isKnownBrowser =
		userAgentLower.includes('firefox') ||
		userAgentLower.includes('chrome') ||
		userAgentLower.includes('safari') ||
		userAgentLower.includes('edge') ||
		userAgentLower.includes('opera');

	const isEmbeddedBrowser = !isKnownBrowser && !(window as any).chrome && !(window as any).safari;

	const result = hasValidMfpState || hasKnownMfpAgent || isEmbeddedBrowser;

	console.log('MFP Environment Detection:', {
		hasValidMfpState,
		stateParam,
		hasKnownMfpAgent,
		isKnownBrowser,
		isEmbeddedBrowser,
		userAgent: navigator.userAgent,
		result,
	});

	return result;
};

export * from './dates.helpers';
export * from './prints.helpers';
export * from './reports.helpers';
export * from './storage.helpers';
